import type {
  JsonCompatibleAxisRange,
  OutliersDisplayMode,
  ReferenceLinesMode,
} from '@neptune/charts-domain';
import type { CompoundSearchCriterion } from '@neptune/recent-searches-domain';
import type { SearchQuery } from '@neptune/search-query-domain';
import { LeaderboardColumn } from '@neptune/shared/leaderboard-domain';
import { SmoothingAlgorithm } from '@neptune/shared/smoothing-domain';
import { AxisScale, XAxisMode } from '@neptune/shared/widgets-domain';

import type { SupportedOs } from 'components/modals/api-token-modal/text-by-os/os-util';
import { TimeScale } from 'domain/common/time-scale';
import { AttributeDefinition } from 'domain/experiment/attribute';
import { ChartGlobalConfig, MetricDistinction } from 'domain/widget';

import {
  LocalStorageLatest,
  parseLocalStorageLatest,
  stringifyLocalStorageLatest,
  wrapAsLocalStorageLatest,
} from '../local-storage';

import { ConvertUndefinedInArraysToNull, DeepComplete } from './utility-types';

export const localStorageTopKey = 'neptune-store-state-v2';

export const loadState = (): LocalStorageState => {
  try {
    const serializedState = localStorage.getItem(localStorageTopKey);
    const { data } = parseLocalStorageLatest(serializedState);
    // If line below causes type mismatch, it means you have introduced a not compatible
    // change to LocalStorageState application model.
    // See local-storage-migration-guide.md for more information.
    return data;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to get state from localStorage.', err);
    return {};
  }
};

export const persistState = (stateChange: Partial<LocalStorageState>) => {
  const oldState = loadState();
  const newState: LocalStorageState = { ...oldState, ...stateChange };
  // If line below causes type mismatch, it means you have introduced a not compatible
  // change to LocalStorageState application model.
  // See local-storage-migration-guide.md for more information.
  const toPersist: LocalStorageLatest = wrapAsLocalStorageLatest(newState);

  try {
    const serializedState = stringifyLocalStorageLatest(toPersist);
    localStorage.setItem(localStorageTopKey, serializedState);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to save state to localStorage.', err);
  }
};

export const clearStorage = () => localStorage.setItem(localStorageTopKey, '');

export type ChartWidgetLocalOptions = {
  xAxisScale?: AxisScale;
  yAxisScale?: AxisScale;
  xAxisMode?: XAxisMode;
  xAxisMetric?: AttributeDefinition;
  yAxisRange?: JsonCompatibleAxisRange;
  xAxisRange?: JsonCompatibleAxisRange;
  metricsStepsRange?: JsonCompatibleAxisRange;
  normalizeToFirstValue?: boolean;
  showCompletion?: boolean;
  smoothing?: number;
  smoothingAlgorithm?: SmoothingAlgorithm;
  showOriginalLineOnSmoothing?: boolean;
  outliersDisplayMode?: OutliersDisplayMode;
};

export type ChartGlobalConfigLocalOptions = {
  xAxisMetric?: AttributeDefinition;
  xAxisMode?: XAxisMode;
  xAxisScale?: AxisScale;
  metricsStepsRange?: JsonCompatibleAxisRange;
  xAxisRange?: JsonCompatibleAxisRange;
  yAxisScale?: AxisScale;
  showCompletion?: boolean;
  normalizeToFirstValue?: boolean;
  smoothing?: number;
  smoothingAlgorithm?: SmoothingAlgorithm;
  showOriginalLineOnSmoothing?: boolean;
  showMetricBy?: MetricDistinction;
  outliersDisplayMode?: OutliersDisplayMode;
};

({}) as ConvertUndefinedInArraysToNull<
  DeepComplete<ChartGlobalConfigLocalOptions>
> satisfies ConvertUndefinedInArraysToNull<DeepComplete<ChartGlobalConfig>>;
({}) as ConvertUndefinedInArraysToNull<
  DeepComplete<ChartGlobalConfig>
> satisfies ConvertUndefinedInArraysToNull<DeepComplete<ChartGlobalConfigLocalOptions>>;

export type LocalStorageState = {
  compareTabByProject?: Record<string, string | undefined>;
  lastDashboardIds?: { [projectPath: string]: string };
  chartWidgetOptionsByProject?: Record<string, Record<string, ChartWidgetLocalOptions>>;
  defaultOrganizationName?: string;
  dontShowBrowserSupportInfo?: boolean;
  dontShowArchiveExperimentHeadConfirmation?: boolean;
  dontShowInviteUsersBanner?: boolean;
  expandableLayout?: Record<string, Record<string, string | undefined> | undefined>;
  lastExperimentDashboardTab?: string;
  orderedColumns?: Record<string, Record<string, LeaderboardColumn[]>>;
  os?: SupportedOs;
  recentMemos?: Record<string, Record<string, string[] | undefined> | undefined>;
  recentSearchQueries?: Record<string, Record<string, SearchQuery[] | undefined> | undefined>;
  recentCompoundQueries?: Record<
    string,
    Record<string, CompoundSearchCriterion[][] | undefined> | undefined
  >;
  stringSeriesTimeScale?: { [attribute: string]: TimeScale | undefined };
  shownHelp?: boolean;
  leaderboardPageSize?: Record<string, number | undefined>;
  bulkAcceptInvitationMuted?: {
    byId: Record<string, number | undefined>;
    projects: Record<string, number | undefined>;
    organizations: Record<string, number | undefined>;
  };
  lbViewIdByProject?: { [projectIdentifier: string]: string | undefined };
  runColorListNameByProject?: { [projectIdentifier: string]: string | undefined };
  textWrapping?: Record<string, Record<string, string | undefined> | undefined>;
  projectLeaderboardSplitPx?: number;
  reportsTableSplitPx?: number;
  attributeViewerSplitRate?: Record<string, number | undefined> | undefined;
  lastProjectTab?: string;
  lastProjectRunsTab?: 'table' | 'details' | 'compare';
  structureAsideCollapsed?: boolean;
  chartGlobalConfig?: Record<string, ChartGlobalConfigLocalOptions>;
  sidebarCollapsed?: boolean;
  dismissedInfoBanners?: Record<string, boolean>;
  dismissedInfoModals?: Record<string, boolean>;
  colors?: Record<string, Record<string, string>>;
  presetColors?: Record<string, string[]>;
  pinWidgetLegend?: boolean;
  errorBandsHidden?: boolean;
  referenceLinesHidden?: boolean;
  referenceLinesMode?: ReferenceLinesMode;
  myRunsTab?: {
    experimentsOnly?: boolean;
    sortDirection?: string;
    sortBy?: string;
    sortFieldType?: string;
    sortFieldAggregationMode?: string;
  };
  attributeViewerSettingsByProject?: Record<string, { outliersDisplayMode?: OutliersDisplayMode }>;
  reportDismissedNewerVersionToasts?: Record<string, number>;
  reportTableHeight?: 'small' | 'medium' | 'large';
  reportOutlineWidthPx?: number;
  reportOutlineOpen?: boolean;
  dashboardOutlineWidthPx?: number;
  dashboardOutlineOpen?: boolean;
  reportSectionFolding?: Record<string, Record<string, boolean>>;
};

// If lines below cause type mismatch, it means you have introduced an incompatible
// change to the LocalStorageState application model.
// See local-storage-migration-guide.md for more information.

({}) as LocalStorageLatest['data'] satisfies LocalStorageState;
({}) as LocalStorageState satisfies LocalStorageLatest['data'];
({}) as DeepComplete<LocalStorageLatest['data']> satisfies DeepComplete<LocalStorageState>;
({}) as DeepComplete<LocalStorageState> satisfies DeepComplete<LocalStorageLatest['data']>;
({}) as ConvertUndefinedInArraysToNull<LocalStorageState> satisfies LocalStorageState;
